import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  Package, 
  Truck, 
  Archive,
  AlertCircle,
  Search,
  Filter,
  FileText,
  Calendar,
  X,
  Loader2,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import { fetchOrders, updateOrderStatus, updateOrder } from '../lib/api';
import type { Order } from '../lib/types';

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<Order['status'] | 'all'>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await fetchOrders();
      setOrders(data);
      setError(null);
    } catch (err) {
      setError('Failed to load orders');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    if (selectedOrder) {
      setNotes(selectedOrder.notes || '');
    }
  }, [selectedOrder]);

  const handleStatusChange = async (orderId: string, newStatus: Order['status']) => {
    try {
      setUpdatingStatus(true);
      setError(null);
      await updateOrderStatus(orderId, newStatus);
      
      // Refresh orders list
      await loadOrders();
      
      // Close modal after successful update
      setSelectedOrder(null);
    } catch (err) {
      setError('Failed to update order status');
      console.error('Error updating order status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedOrder) return;

    try {
      setSavingNotes(true);
      setError(null);

      await updateOrder({
        ...selectedOrder,
        notes
      });

      // Refresh orders list
      await loadOrders();

      // Update selected order with new notes
      setSelectedOrder(prev => prev ? { ...prev, notes } : null);
    } catch (err) {
      setError('Failed to save notes');
      console.error('Error saving notes:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'fulfillment':
        return 'bg-purple-100 text-purple-800';
      case 'pending_delivery':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return Clock;
      case 'accepted':
        return CheckCircle;
      case 'cancelled':
        return AlertCircle;
      case 'fulfillment':
        return Package;
      case 'pending_delivery':
        return Truck;
      case 'completed':
        return Archive;
      default:
        return Clock;
    }
  };

  const calculateOrderTotal = (order: Order) => {
    return order.items.reduce((total, item) => {
      const itemTotal = item.priceAtTime * item.quantity;
      const discount = itemTotal * (item.discountPercentage / 100);
      return total + (itemTotal - discount);
    }, 0);
  };

  const getTotalQuantity = (order: Order): number => {
    return order.items.reduce((total, item) => total + item.quantity, 0);
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items.some(item => 
        item.product.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.product.sku.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesArchived = order.archived === showArchived;
    
    return matchesSearch && matchesStatus && matchesArchived;
  });

  if (loading) {
    return <div className="text-center py-8">Loading orders...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        {error}
        <button
          onClick={loadOrders}
          className="block mx-auto mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Orders</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Order['status'] | 'all')}
            className="border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="cancelled">Cancelled</option>
            <option value="fulfillment">Fulfillment</option>
            <option value="pending_delivery">Pending Delivery</option>
            <option value="completed">Completed</option>
          </select>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center space-x-2 text-gray-600 hover:text-green-600"
          >
            {showArchived ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            <span>{showArchived ? 'Hide Archived' : 'Show Archived'}</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredOrders.map((order) => {
              const StatusIcon = getStatusIcon(order.status);
              const total = calculateOrderTotal(order);
              const totalQty = getTotalQuantity(order);
              
              return (
                <tr 
                  key={order.id} 
                  className={`hover:bg-gray-50 cursor-pointer ${order.archived ? 'opacity-75' : ''}`}
                  onClick={() => setSelectedOrder(order)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{order.id}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                      {order.archived && (
                        <span className="ml-2 text-gray-500">
                          (Archived: {new Date(order.archivedAt!).toLocaleDateString()})
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{order.customerName}</div>
                    <div className="text-sm text-gray-500">
                      {totalQty} units ordered
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">
                      ${total.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      <StatusIcon className="w-4 h-4 mr-2" />
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1).replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Order Details
                </h3>
                <p className="text-gray-600">Order #{selectedOrder.id}</p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Customer</h4>
                <p className="text-lg font-medium text-gray-900">{selectedOrder.customerName}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Order Date</h4>
                <p className="text-lg font-medium text-gray-900">
                  {new Date(selectedOrder.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Status</h4>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value as Order['status'])}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
                    disabled={updatingStatus || selectedOrder.archived}
                  >
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="fulfillment">Fulfillment</option>
                    <option value="pending_delivery">Pending Delivery</option>
                    <option value="completed">Completed</option>
                  </select>
                  {updatingStatus && (
                    <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                  )}
                </div>
              </div>
              {selectedOrder.completedAt && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Completed Date</h4>
                  <p className="text-lg font-medium text-gray-900">
                    {new Date(selectedOrder.completedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Order Items</h4>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Product</th>
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Quantity</th>
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Price</th>
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Discount</th>
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {selectedOrder.items.map((item) => {
                      const itemTotal = item.priceAtTime * item.quantity;
                      const discount = itemTotal * (item.discountPercentage / 100);
                      const total = itemTotal - discount;

                      return (
                        <tr key={item.id}>
                          <td className="py-4 pl-4 pr-3 text-sm">
                            <div className="font-medium text-gray-900">{item.product.product}</div>
                            <div className="text-gray-500">
                              SKU: {item.product.sku} • Brand: {item.product.brand}
                            </div>
                            {item.product.unitSize && (
                              <div className="text-gray-500">
                                Unit Size: {item.product.unitSize}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-4 text-sm text-right text-gray-500">
                            {item.quantity}
                          </td>
                          <td className="px-3 py-4 text-sm text-right text-gray-500">
                            ${item.priceAtTime.toFixed(2)}
                          </td>
                          <td className="px-3 py-4 text-sm text-right text-gray-500">
                            {item.discountPercentage}%
                          </td>
                          <td className="px-3 py-4 text-sm text-right font-medium text-gray-900">
                            ${total.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <th scope="row" colSpan={4} className="py-3.5 pl-4 pr-3 text-right text-sm font-semibold text-gray-900">
                        Total
                      </th>
                      <td className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        ${calculateOrderTotal(selectedOrder).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700">Order Notes</h4>
                {!selectedOrder.archived && (
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="flex items-center space-x-2 text-green-600 hover:text-green-700 disabled:opacity-50"
                  >
                    {savingNotes ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>Save Notes</span>
                  </button>
                )}
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Add notes about this order..."
                disabled={selectedOrder.archived}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;